/**
 * mSTEM Share Project Modal (PRJ-04 / LLD UC 11). Presentational component
 * — Redux wiring lives in src/containers/share-project-modal.jsx.
 *
 * LLD UC 11 mandated fields (STT 1-7):
 *   1. Tên dự án (read-only — current project title)
 *   2. Đối tượng chia sẻ (dropdown — teacher / class / community)
 *   3. Quyền truy cập (radio — EXACTLY 2 options per DP-09: Xem | Sao chép)
 *   4. Lời nhắn / Mô tả dự án (textarea, optional)
 *   5. Khối thông báo kết quả (inline error/success area — implicit via
 *      Step 2 success banner + per-field error spans)
 *   6. Nút Hủy bỏ (secondary)
 *   7. Nút Xác nhận chia sẻ (primary navy)
 *
 * Step 2 (after successful submit) reveals:
 *   - Read-only mock share link input (https://mstem.mobifone.vn/share/[8c])
 *   - "Sao chép link" button (cyan) that toggles to "✓ Đã sao chép" for 2s
 *
 * Form / strings live here so the Phase 3 aggregated i18n test
 * (test/unit/i18n/phase-3-messages.test.js) can pick them up by id during
 * extraction.
 */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './share-project-modal.css';

// NEW IDs unique to this plan (PRJ-04) — extracted by babel react-intl
// plugin since they live inside defineMessages().
const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Chia sẻ dự án',
        description: 'Share Project modal title (PRJ-04 / LLD UC 11)',
        id: 'gen.share.modal.title'
    },
    fieldProjectName: {
        defaultMessage: 'Tên dự án',
        description: 'Read-only project name label in ShareProjectModal',
        id: 'gen.share.field.projectName'
    },
    fieldAudience: {
        defaultMessage: 'Đối tượng chia sẻ',
        description: 'Audience dropdown label (PRJ-04 LLD UC 11 STT 2)',
        id: 'gen.share.field.audience'
    },
    audiencePlaceholder: {
        defaultMessage: 'Chọn đối tượng...',
        description: 'Placeholder in audience dropdown (PRJ-04)',
        id: 'gen.share.audience.placeholder'
    },
    audienceGroupTeacher: {
        defaultMessage: 'Giáo viên',
        description: 'Optgroup label: teachers (PRJ-04)',
        id: 'gen.share.audience.teacher'
    },
    audienceGroupClass: {
        defaultMessage: 'Lớp học',
        description: 'Optgroup label: classes (PRJ-04)',
        id: 'gen.share.audience.class'
    },
    audienceCommunity: {
        defaultMessage: 'Công khai - Cộng đồng MBF',
        description: 'Public community option in audience dropdown (PRJ-04)',
        id: 'gen.share.audience.community'
    },
    fieldPermission: {
        defaultMessage: 'Quyền truy cập',
        description: 'Permission radio group label (PRJ-04 LLD UC 11 STT 3)',
        id: 'gen.share.field.permission'
    },
    permissionView: {
        defaultMessage: 'Xem',
        description: 'Permission radio: view only (PRJ-04 DP-09)',
        id: 'gen.share.permission.view'
    },
    permissionViewHelp: {
        defaultMessage: 'Người nhận chỉ có thể xem dự án',
        description: 'Help text under view-only radio (PRJ-04)',
        id: 'gen.share.permission.viewHelp'
    },
    permissionCopy: {
        defaultMessage: 'Sao chép bản sao',
        description: 'Permission radio: allow copy (PRJ-04 DP-09)',
        id: 'gen.share.permission.copy'
    },
    permissionCopyHelp: {
        defaultMessage: 'Người nhận có thể sao chép về không gian cá nhân',
        description: 'Help text under copy radio (PRJ-04)',
        id: 'gen.share.permission.copyHelp'
    },
    fieldMessage: {
        defaultMessage: 'Lời nhắn / Mô tả dự án',
        description: 'Optional message/description textarea label (PRJ-04 LLD UC 11 STT 4)',
        id: 'gen.share.field.message'
    },
    messagePlaceholder: {
        defaultMessage: 'Lời nhắn cho người nhận (tùy chọn)',
        description: 'Placeholder text in message textarea (PRJ-04)',
        id: 'gen.share.message.placeholder'
    },
    confirmShare: {
        defaultMessage: 'Xác nhận chia sẻ',
        description: 'Submit share form button label (PRJ-04 LLD UC 11 STT 7)',
        id: 'gen.share.confirm'
    },
    closeButton: {
        defaultMessage: 'Đóng',
        description: 'Close button in step 2 of ShareProjectModal (PRJ-04)',
        id: 'gen.share.close'
    },
    // Step 2 strings
    step2Heading: {
        defaultMessage: 'Dự án đã được chia sẻ. Link truy cập:',
        description: 'Heading shown on step 2 link-reveal of ShareProjectModal (PRJ-04)',
        id: 'gen.share.step2.heading'
    },
    linkLabel: {
        defaultMessage: 'Link chia sẻ dự án',
        description: 'aria-label on the read-only share link input (PRJ-04)',
        id: 'gen.share.link.label'
    },
    copyLink: {
        defaultMessage: 'Sao chép link',
        description: 'Copy share link button label (PRJ-04 DP-06)',
        id: 'gen.share.linkCopy'
    },
    copiedLabel: {
        defaultMessage: '✓ Đã sao chép',
        description: 'Temporary label on Copy button after successful copy (PRJ-04)',
        id: 'gen.share.linkCopied'
    },
    // Error states
    errAudienceRequired: {
        defaultMessage: 'Vui lòng chọn đối tượng chia sẻ',
        description: 'Inline error: audience required (PRJ-04)',
        id: 'gen.share.error.audienceRequired'
    },
    errPermissionRequired: {
        defaultMessage: 'Vui lòng chọn quyền truy cập',
        description: 'Inline error: permission required (PRJ-04)',
        id: 'gen.share.error.permissionRequired'
    }
});

// REUSED IDs from Plan 03-02 new-project-modal — declared as plain object
// literal (NOT inside defineMessages) so babel react-intl plugin SKIPS them
// during extraction. Avoids openblock-l10n build-i18n-src "Duplicate
// message id with different content" guard. Mirrors Plan 03-03/04 pattern.
const reused = {
    cancel: {
        defaultMessage: 'Hủy bỏ',
        id: 'gen.project.action.cancel'
    }
};

const renderAudienceOptions = (audiences, intl) => [
    <option
        key="placeholder"
        value=""
    >
        {intl.formatMessage(messages.audiencePlaceholder)}
    </option>,
    <optgroup
        key="teachers"
        label={intl.formatMessage(messages.audienceGroupTeacher)}
    >
        {audiences.teachers.map(t => (
            <option
                key={t.id}
                value={`teacher:${t.id}`}
            >{t.name}</option>
        ))}
    </optgroup>,
    <optgroup
        key="classes"
        label={intl.formatMessage(messages.audienceGroupClass)}
    >
        {audiences.classes.map(c => (
            <option
                key={c.id}
                value={`class:${c.id}`}
            >{c.name}</option>
        ))}
    </optgroup>,
    <option
        key="community"
        value={`community:${audiences.community.id}`}
    >
        {intl.formatMessage(messages.audienceCommunity)}
    </option>
];

const Step1Form = ({
    intl, projectName, audience, permission, message, errors, audiences,
    onChangeAudience, onChangePermission, onChangeMessage, onSubmit, onCancel
}) => (
    <form
        className={styles.editForm}
        noValidate
        onSubmit={onSubmit}
    >
        <div className={styles.formField}>
            <label
                className={styles.formLabel}
                htmlFor="sp-project-name"
            >
                <FormattedMessage {...messages.fieldProjectName} />
            </label>
            <input
                readOnly
                className={styles.formInput}
                id="sp-project-name"
                type="text"
                value={projectName}
            />
        </div>

        <div className={styles.formField}>
            <label
                className={styles.formLabel}
                htmlFor="sp-audience"
            >
                <FormattedMessage {...messages.fieldAudience} />
                <span className={styles.required}>{'*'}</span>
            </label>
            <select
                aria-invalid={Boolean(errors.audience)}
                className={classNames(styles.formInput, {
                    [styles.formInputError]: errors.audience
                })}
                id="sp-audience"
                value={audience}
                onChange={onChangeAudience}
            >
                {renderAudienceOptions(audiences, intl)}
            </select>
            {errors.audience ? (
                <span className={styles.formError}>
                    <FormattedMessage {...messages[errors.audience]} />
                </span>
            ) : null}
        </div>

        <fieldset className={styles.formFieldset}>
            <legend className={styles.formLabel}>
                <FormattedMessage {...messages.fieldPermission} />
                <span className={styles.required}>{'*'}</span>
            </legend>
            <label className={styles.radioRow}>
                <input
                    checked={permission === 'view'}
                    name="sp-permission"
                    type="radio"
                    value="view"
                    onChange={onChangePermission}
                />
                <span className={styles.radioLabel}>
                    <FormattedMessage {...messages.permissionView} />
                    <span className={styles.radioHelp}>
                        <FormattedMessage {...messages.permissionViewHelp} />
                    </span>
                </span>
            </label>
            <label className={styles.radioRow}>
                <input
                    checked={permission === 'copy'}
                    name="sp-permission"
                    type="radio"
                    value="copy"
                    onChange={onChangePermission}
                />
                <span className={styles.radioLabel}>
                    <FormattedMessage {...messages.permissionCopy} />
                    <span className={styles.radioHelp}>
                        <FormattedMessage {...messages.permissionCopyHelp} />
                    </span>
                </span>
            </label>
            {errors.permission ? (
                <span className={styles.formError}>
                    <FormattedMessage {...messages[errors.permission]} />
                </span>
            ) : null}
        </fieldset>

        <div className={styles.formField}>
            <label
                className={styles.formLabel}
                htmlFor="sp-message"
            >
                <FormattedMessage {...messages.fieldMessage} />
            </label>
            <textarea
                className={classNames(styles.formInput, styles.formTextarea)}
                id="sp-message"
                maxLength={2000}
                placeholder={intl.formatMessage(messages.messagePlaceholder)}
                rows={3}
                value={message}
                onChange={onChangeMessage}
            />
        </div>

        <div className={styles.formActions}>
            <button
                className={styles.btnSecondary}
                type="button"
                onClick={onCancel}
            >
                <FormattedMessage {...reused.cancel} />
            </button>
            <button
                className={styles.btnPrimary}
                type="submit"
            >
                <FormattedMessage {...messages.confirmShare} />
            </button>
        </div>
    </form>
);

Step1Form.propTypes = {
    audience: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    audiences: PropTypes.object.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    message: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChangeAudience: PropTypes.func.isRequired,
    onChangeMessage: PropTypes.func.isRequired,
    onChangePermission: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    permission: PropTypes.string.isRequired,
    projectName: PropTypes.string.isRequired
};

const Step2Link = ({intl, link, copyState, onCopy, onClose}) => (
    <div className={styles.linkRevealBody}>
        <div
            className={styles.successBanner}
            role="status"
        >
            <FormattedMessage {...messages.step2Heading} />
        </div>
        <div className={styles.linkRow}>
            <input
                readOnly
                aria-label={intl.formatMessage(messages.linkLabel)}
                className={styles.linkInput}
                type="text"
                value={link}
            />
            <button
                className={classNames(styles.btnPrimary, styles.btnCyan)}
                disabled={copyState === 'copying'}
                type="button"
                onClick={onCopy}
            >
                {copyState === 'copied' ? (
                    <FormattedMessage {...messages.copiedLabel} />
                ) : (
                    <FormattedMessage {...messages.copyLink} />
                )}
            </button>
        </div>
        <div className={styles.formActions}>
            <button
                className={styles.btnSecondary}
                type="button"
                onClick={onClose}
            >
                <FormattedMessage {...messages.closeButton} />
            </button>
        </div>
    </div>
);

Step2Link.propTypes = {
    copyState: PropTypes.oneOf(['idle', 'copying', 'copied', 'failed']).isRequired,
    intl: intlShape.isRequired,
    link: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired
};

const ShareProjectModalComponent = props => (
    <Modal
        className={styles.modalContent}
        contentLabel={props.intl.formatMessage(messages.modalTitle)}
        onRequestClose={props.onClose}
    >
        <div className={styles.body}>
            {props.step === 1 ? <Step1Form {...props} /> : <Step2Link {...props} />}
        </div>
    </Modal>
);

ShareProjectModalComponent.propTypes = {
    audience: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    audiences: PropTypes.object.isRequired,
    copyState: PropTypes.oneOf(['idle', 'copying', 'copied', 'failed']),
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
    link: PropTypes.string,
    message: PropTypes.string.isRequired,
    onCancel: PropTypes.func.isRequired,
    onChangeAudience: PropTypes.func.isRequired,
    onChangeMessage: PropTypes.func.isRequired,
    onChangePermission: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onCopy: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    permission: PropTypes.string.isRequired,
    projectName: PropTypes.string.isRequired,
    step: PropTypes.oneOf([1, 2]).isRequired
};

ShareProjectModalComponent.defaultProps = {
    copyState: 'idle',
    link: ''
};

export default injectIntl(ShareProjectModalComponent);
